import React, { useState, useEffect } from 'react';
import { useNavigate, useLoaderData, Navigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { createJob, uploadJobPicture } from '../api/job';
import { fetchCategories } from '../api/contractorProfile';
import { fetchProvinces, fetchTowns, Province, Town } from '../api/locations';
import { Category } from '../types/contractor';
import { useAuth } from '../context/AuthContext';

export async function createJobLoader() {
  const [categories, provinces] = await Promise.all([fetchCategories(), fetchProvinces()]);
  return { categories, provinces };
}

const CreateJobPage: React.FC = () => {
  const { categories, provinces } = useLoaderData() as { categories: Category[]; provinces: Province[] };
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [provinceId, setProvinceId] = useState('');
  const [townId, setTownId] = useState('');
  const [towns, setTowns] = useState<Town[]>([]);
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleCategory = (id: number) => {
    setSelectedCategoryIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    setTownId('');
    if (provinceId) {
      fetchTowns(parseInt(provinceId)).then(setTowns).catch(() => setTowns([]));
    } else {
      setTowns([]);
    }
  }, [provinceId]);

  if (currentUser?.isContractor) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const selectedTown = towns.find(t => t.id === parseInt(townId));
    const selectedProvince = provinces.find(p => p.id === parseInt(provinceId));
    const locationParts = [selectedTown?.name, selectedProvince?.name].filter(Boolean);
    const location = locationParts.join(', ') || '';

    if (!location) {
      setError('Please select a province and town.');
      return;
    }

    if (selectedCategoryIds.length === 0) {
      setError('Please select at least one category.');
      return;
    }

    setSubmitting(true);
    try {
      let pictureUrl: string | undefined;
      if (pictureFile) {
        pictureUrl = await uploadJobPicture(pictureFile);
      }
      const job = await createJob({
        title,
        description,
        categoryIds: selectedCategoryIds,
        townId: townId ? parseInt(townId) : undefined,
        location,
        budgetMin: budgetMin ? parseFloat(budgetMin) : undefined,
        budgetMax: budgetMax ? parseFloat(budgetMax) : undefined,
        pictureUrl,
      });
      navigate(`/my-jobs`);
    } catch (e: any) {
      setError(e.message ?? 'Failed to post job.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full p-3 rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary';
  const labelClass = 'block font-label-bold text-on-surface mb-1.5';

  return (
    <div className="bg-surface text-on-surface min-h-screen pb-24 md:pb-0">
      <TopAppBar />

      <main className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-secondary font-label-bold mb-6 hover:underline"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back
        </button>

        <h1 className="font-headline-md text-primary mb-8">Post a Job</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={labelClass}>Job title *</label>
            <input
              type="text"
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Bathroom renovation in Sea Point"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Description *</label>
            <textarea
              required
              maxLength={2000}
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the work needed, any special requirements, timeline expectations..."
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Categories <span className="text-error font-normal">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => {
                const selected = selectedCategoryIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      selected
                        ? 'bg-primary text-on-primary border-primary'
                        : 'bg-surface text-on-surface-variant border-outline-variant hover:border-primary hover:text-primary'
                    }`}
                  >
                    {selected && <span className="mr-1">✓</span>}
                    {c.name}
                  </button>
                );
              })}
            </div>
            {selectedCategoryIds.length === 0 && (
              <p className="text-xs text-on-surface-variant mt-1.5">Select at least one category.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Province *</label>
              <select
                required
                value={provinceId}
                onChange={(e) => setProvinceId(e.target.value)}
                className={inputClass}
              >
                <option value="">Select a province</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Town *</label>
              <select
                required
                value={townId}
                onChange={(e) => setTownId(e.target.value)}
                disabled={towns.length === 0}
                className={inputClass}
              >
                <option value="">Select a town</option>
                {towns.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Budget min (R, optional)</label>
              <input
                type="number"
                min="0"
                step="500"
                value={budgetMin}
                onChange={(e) => setBudgetMin(e.target.value)}
                placeholder="e.g. 5000"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Budget max (R, optional)</label>
              <input
                type="number"
                min="0"
                step="500"
                value={budgetMax}
                onChange={(e) => setBudgetMax(e.target.value)}
                placeholder="e.g. 20000"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Photo (optional)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setPictureFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-on-surface-variant file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-label-bold file:bg-primary file:text-on-primary hover:file:bg-primary/90"
            />
            {pictureFile && (
              <p className="mt-1 text-xs text-on-surface-variant">{pictureFile.name}</p>
            )}
          </div>

          {error && <p className="text-error text-sm">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-on-primary py-3 rounded-lg font-label-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </button>
        </form>
      </main>

      <BottomNavBar />
    </div>
  );
};

export default CreateJobPage;
